import { Badge } from '@/global/components/ui/badge'
import { Tooltip } from '@/global/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { TrocaInicioPonto } from '@/service/api'
import dayjs from 'dayjs'
import { ArrowLeftRightIcon, ArrowRightIcon, LockIcon } from 'lucide-react'
import React, {
    type PointerEvent as ReactPointerEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useFormContext } from 'react-hook-form'
import { useEditarEmpresaOutletContext } from '../../../modals/Editar'
import { calcularCompetencias } from './calcularCompetencias'

const LIMITE_DIAS = 45

interface IProps {
    trocasInicioPonto: TrocaInicioPonto[]
    onValidityChange: (valido: boolean) => void
}

const clamp = (v: number, min: number, max: number) =>
    Math.min(Math.max(v, min), max)
const diasEntre = (a: dayjs.Dayjs, b: dayjs.Dayjs) => b.diff(a, 'day') + 1

// listras de "limite atingido": aparecem junto da divisa só quando ela bate no
// teto de 45 dias, com gradiente de opacidade que esmaece para longe da divisa
const PADRAO_LISTRAS =
    'repeating-linear-gradient(45deg, var(--warning) 0 6px, transparent 6px 12px)'
const fade = (lado: 'left' | 'right') => ({
    backgroundImage: PADRAO_LISTRAS,
    maskImage: `linear-gradient(to ${lado}, #000, transparent)`,
    WebkitMaskImage: `linear-gradient(to ${lado}, #000, transparent)`,
})

// distribui as colunas pelo nº de dias (flex-basis 0 → proporção pelo peso)
const peso = (dias: number) => ({ flexGrow: dias, flexBasis: 0, minWidth: 0 })

// fechada e futura são apenas contexto: caixas compactas de largura fixa, uma
// em cada ponta. atual e transição ocupam o miolo, proporcionais aos dias.
const PESO_CONTEXTO = 16

// abaixo deste % de largura o segmento fica estreito demais para o texto: passa
// a mostrar só um resumo e leva o conteúdo completo para um tooltip (hover/foco)
const LIMIAR_ESTREITO = 13

/** Conteúdo padrão de tooltip/resumo de uma competência. */
function DetalheComp({
    titulo,
    periodo,
    nota,
}: {
    titulo: React.ReactNode
    periodo?: React.ReactNode
    nota?: React.ReactNode
}) {
    return (
        <div className="space-y-0.5 text-center">
            <div className="font-semibold">{titulo}</div>
            {periodo && (
                <div className="text-secondary-foreground">{periodo}</div>
            )}
            {nota && <div className="text-warning">{nota}</div>}
        </div>
    )
}

/**
 * Um segmento (competência) da linha do tempo. Se estiver estreito demais para
 * o texto, exibe `compact` e move o conteúdo completo para um tooltip.
 */
function Segmento({
    pesoDias,
    estreito,
    contexto = false,
    tooltip,
    compact,
    children,
}: {
    pesoDias: number
    estreito: boolean
    contexto?: boolean
    tooltip?: React.ReactNode
    compact?: React.ReactNode
    children: React.ReactNode
}) {
    const className = cn(
        'flex flex-col items-center justify-center gap-1 px-2 text-center',
        contexto && 'bg-muted text-secondary-foreground',
        estreito && 'cursor-help',
    )

    if (estreito && tooltip) {
        return (
            <Tooltip title={tooltip} z={2000}>
                <div
                    className={cn('select-none', className)}
                    style={peso(pesoDias)}
                >
                    {compact}
                </div>
            </Tooltip>
        )
    }

    return (
        <div className={cn('select-none', className)} style={peso(pesoDias)}>
            {children}
        </div>
    )
}

/** Linha vertical de uma divisa entre dois segmentos. */
function LinhaDivisa({ frac, primary }: { frac: number; primary?: boolean }) {
    return (
        <div
            className={cn(
                'pointer-events-none absolute',
                'inset-y-0 w-0.5 -translate-x-1/2',
                'bg-primary-foreground',
                'transition-colors duration-200',
                {
                    ' group-hover:bg-primary': primary,
                    'bg-border': !primary,
                },
            )}
            style={{ left: `${frac}%` }}
        />
    )
}

/** Selo circular de uma divisa travada (não arrastável), com cadeado. */
function SeloTravado({ frac }: { frac: number }) {
    return (
        <span
            className="pointer-events-none absolute top-1/2 z-10 flex size-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border bg-background text-secondary-foreground"
            style={{ left: `${frac}%` }}
        >
            <LockIcon className="size-4" />
        </span>
    )
}

/** Rótulo de datas de uma divisa. `posicao` controla acima/abaixo da caixa. */
function RotuloDivisa({
    frac,
    posicao,
    datas,
    legenda,
}: {
    frac: number
    posicao: 'topo' | 'base'
    datas: React.ReactNode
    legenda: React.ReactNode
}) {
    return (
        <div
            className={cn(
                'absolute -translate-x-1/2 whitespace-nowrap text-center',
                'select-none text-sm',
                posicao === 'topo' ? 'bottom-full mb-2' : 'top-full mt-2',
            )}
            style={{ left: `${frac}%` }}
        >
            <div className="text-xs font-semibold">{datas}</div>
            <div className="text-[11px] text-secondary-foreground">
                {legenda}
            </div>
        </div>
    )
}

/**
 * Edição por linha do tempo (uma divisa arrastável).
 *
 * A fronteira contra a competência fechada fica travada (início da atual = fixo)
 * e a fronteira transição↔futura é definida pelo "Novo dia de início do ponto"
 * (muda só quando o dia muda). A ÚNICA divisa arrastável é a atual↔transição: o
 * fim da atual e o início da transição mudam juntos, sem deixar dia perdido.
 *
 * Escreve no formulário (igual aos componentes irmãos):
 *   - dataInicial        → início da atual (fixo, travado pela fechada)
 *   - dataFinal          → fim da atual (arrastável)
 *   - proximaDataInicial → início da transição (arrastável)
 *   - proximaDataFinal   → fim da transição (fixo pelo novo dia de início)
 *   - onValidityChange(boolean) → habilita/desabilita o submit
 */
export default function EdicaoSlider({
    trocasInicioPonto,
    onValidityChange,
}: IProps) {
    const { watch, setValue } = useFormContext()
    const {
        data: { competenciaAtual: competenciaAtualRaw, diaInicioPonto = 1 },
    } = useEditarEmpresaOutletContext()
    const competenciaAtual = competenciaAtualRaw ?? ''
    const novoDiaInicioPonto = Number(watch('diaInicioPonto', diaInicioPonto))
    const diaAnterior = Number(watch('diaAnterior', diaInicioPonto))

    // O pai re-renderiza a cada setValue; sem o ref o callback inline mudaria de
    // identidade e o efeito de validação dispararia em loop.
    const onValidityChangeRef = useRef(onValidityChange)
    onValidityChangeRef.current = onValidityChange

    const base = useMemo(() => {
        const calc = calcularCompetencias({
            competenciaAtual,
            diaInicioPonto,
            novoDiaInicioPonto: Number(novoDiaInicioPonto) || diaInicioPonto,
            maximoDias: 31,
            trocasInicioPonto,
        })
        const [atual, transicao, futuro] = calc

        const anteriorInicio = atual.inicio
            .subtract(1, 'month')
            .set('date', diaAnterior)
        const anteriorFim = atual.inicio.subtract(1, 'day')
        const anterior = {
            competencia: dayjs(atual.competencia, 'YYYY-MM')
                .subtract(1, 'month')
                .format('YYYY-MM'),
            inicio: anteriorInicio,
            fim: anteriorFim,
            dias: diasEntre(anteriorInicio, anteriorFim),
        }

        return {
            anterior,
            atualComp: atual.competencia,
            transComp: transicao.competencia,
            futuraComp: futuro.competencia,
            futuraDias: diasEntre(futuro.inicio, futuro.fim),
            start: atual.inicio, // início da atual (fixo, travado pela fechada)
            transFim: transicao.fim, // fim da transição (fixo pelo novo dia)
            futuraInicio: futuro.inicio,
            futuraFim: futuro.fim,
            iniAtual: diasEntre(atual.inicio, atual.fim),
        }
    }, [
        competenciaAtual,
        diaInicioPonto,
        novoDiaInicioPonto,
        trocasInicioPonto,
        diaAnterior,
    ])

    const { start, transFim, futuraDias } = base

    // dias entre o início da atual e o fim (fixo) da transição. A divisa divide
    // esse intervalo: o que fica à esquerda é a atual, à direita é a transição.
    const bTrans = diasEntre(start, transFim)

    // b1 = dias na atual. Única divisa arrastável (atual↔transição).
    const [b1, setB1] = useState(base.iniAtual)
    useEffect(() => setB1(base.iniAtual), [base.iniAtual])

    // limites: cada competência entre 1 e 45 dias
    const b1Min = Math.max(1, bTrans - LIMITE_DIAS)
    const b1Max = Math.min(LIMITE_DIAS, bTrans - 1)

    const atualDias = b1
    const transDias = bTrans - b1
    const atualFim = start.add(b1 - 1, 'day')
    const transInicio = start.add(b1, 'day')
    const futuraInicio = base.futuraInicio
    const diaVigente = futuraInicio.date() // dia em que o novo ciclo começa

    // avisos de tamanho
    const atualNoLimite = atualDias >= LIMITE_DIAS
    const transNoLimite = transDias >= LIMITE_DIAS

    // Escala: contexto fixo nas pontas, miolo (atual+transição) linear em dias.
    // TOTAL não depende de b1, então arrastar a divisa não reescala o resto.
    const TOTAL = 2 * PESO_CONTEXTO + bTrans
    const pct = (w: number) => (w / TOTAL) * 100
    const fracFechada = pct(PESO_CONTEXTO) // linha travada (lock)
    const fracDivisa1 = pct(PESO_CONTEXTO + b1) // atual↔transição (arrastável)
    const fracDivisa2 = pct(PESO_CONTEXTO + bTrans) // transição↔futura (fixa)

    // segmentos estreitos demais → resumo + tooltip
    const estreitoCtx = pct(PESO_CONTEXTO) < LIMIAR_ESTREITO
    const estreitoAtual = pct(atualDias) < LIMIAR_ESTREITO
    const estreitoTrans = pct(transDias) < LIMIAR_ESTREITO

    const limiteEsq = b1 <= b1Min
    const limiteDir = b1 >= b1Max

    // Mapeia uma data para a sua posição (%) na linha do tempo, usando os MESMOS
    // pct dos segmentos: fechada/futura comprimidas, miolo linear em dias.
    const dataParaPct = (d: dayjs.Dayjs) => {
        if (d.isBefore(start))
            return pct(
                (PESO_CONTEXTO * d.diff(base.anterior.inicio, 'day')) /
                    base.anterior.dias,
            )
        if (!d.isAfter(transFim))
            return pct(PESO_CONTEXTO + d.diff(start, 'day'))
        return pct(
            PESO_CONTEXTO +
                bTrans +
                (PESO_CONTEXTO * d.diff(transFim, 'day')) / futuraDias,
        )
    }

    // régua: do começo da fechada ao fim da futura
    const primeiroMes = base.anterior.inicio.startOf('month')
    const totalMeses =
        base.futuraFim.startOf('month').diff(primeiroMes, 'month') + 1
    const meses = Array.from({ length: Math.max(totalMeses, 1) }, (_, i) =>
        primeiroMes.add(i, 'month'),
    )

    // propaga datas para o formulário. dataInicial e proximaDataFinal são fixos
    // (cycle start e novo dia); dataFinal/proximaDataInicial seguem a divisa.
    useEffect(() => {
        setValue('dataInicial', start.toDate())
        setValue('dataFinal', atualFim.toDate())
        setValue('proximaDataInicial', transInicio.toDate())
        setValue('proximaDataFinal', transFim.toDate())
        onValidityChangeRef.current(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [b1, base, setValue])

    // --- arraste da divisa: a linha e o botão saem da MESMA fração ---
    const caixaRef = useRef<HTMLDivElement>(null)
    const arrastando = useRef(false)

    const ponteiroParaOffset = (clientX: number) => {
        const el = caixaRef.current
        if (!el) return null
        const rect = el.getBoundingClientRect()
        return Math.round(
            ((clientX - rect.left) / rect.width) * TOTAL - PESO_CONTEXTO,
        )
    }
    const mover = (clientX: number) => {
        const v = ponteiroParaOffset(clientX)
        if (v == null) return
        setB1(clamp(v, b1Min, b1Max))
    }
    const handlers = {
        onPointerDown: (e: ReactPointerEvent) => {
            arrastando.current = true
            e.currentTarget.setPointerCapture(e.pointerId)
            mover(e.clientX)
        },
        onPointerMove: (e: ReactPointerEvent) => {
            if (!arrastando.current) return
            mover(e.clientX)
        },
        onPointerUp: (e: ReactPointerEvent) => {
            arrastando.current = false
            e.currentTarget.releasePointerCapture(e.pointerId)
        },
        onKeyDown: (e: React.KeyboardEvent) => {
            const delta =
                e.key === 'ArrowLeft' || e.key === 'ArrowDown'
                    ? -1
                    : e.key === 'ArrowRight' || e.key === 'ArrowUp'
                      ? 1
                      : 0
            if (!delta) return
            setB1(o => clamp(o + delta, b1Min, b1Max))
        },
    }

    if (!start.isValid()) {
        return (
            <p className="rounded-lg border border-dashed p-3 text-sm text-secondary-foreground">
                Não foi possível montar a linha do tempo: competência atual
                indisponível.
            </p>
        )
    }

    const mmAnterior = dayjs(base.anterior.competencia, 'YYYY-MM')
    const mmAtual = dayjs(base.atualComp, 'YYYY-MM')
    const mmTrans = dayjs(base.transComp, 'YYYY-MM')

    return (
        <div className="flex flex-col gap-8">
            {/* Linha do tempo: régua de meses, caixa de segmentos e divisas.
                Rótulos das divisas travadas ficam ACIMA da caixa; o da divisa
                arrastável fica ABAIXO (acompanha o arraste). */}
            <div>
                {/* Régua de meses — rótulos e marcas alinhados ao fim de cada mês
                    dentro da linha do tempo (mesmos pct dos segmentos) */}
                <div className="relative h-9 select-none">
                    {meses.map((m, i) => {
                        const ini = clamp(dataParaPct(m), 0, 100)
                        const prox = meses[i + 1]
                        const fim = prox
                            ? clamp(dataParaPct(prox), 0, 100)
                            : 100
                        return (
                            <span
                                key={m.format('YYYY-MM')}
                                className="absolute top-0 -translate-x-1/2 text-xs uppercase tracking-wide text-secondary-foreground"
                                style={{ left: `${(ini + fim) / 2}%` }}
                            >
                                {m.format('MMM')}
                            </span>
                        )
                    })}
                    {/* linha base */}
                    <div className="absolute inset-x-0 top-5 border-t border-border" />
                    {/* marcas no início de cada mês + extremidades */}
                    {[0, ...meses.map(m => clamp(dataParaPct(m), 0, 100)), 100]
                        .filter((v, i, a) => a.indexOf(v) === i)
                        .map(v => (
                            <div
                                key={v}
                                className="absolute top-5 h-2 w-px -translate-x-1/2 bg-border"
                                style={{ left: `${v}%` }}
                            />
                        ))}
                </div>

                {/* Caixa com os segmentos + divisas. mt/mb dão espaço aos rótulos
                    (acima das travadas, abaixo da arrastável). */}
                <div className="relative mt-12 mb-10 rounded-xl border">
                    {/* Caixa recortada. A borda fica no wrapper para que segmentos
                        e divisas compartilhem a mesma origem. */}
                    <div
                        ref={caixaRef}
                        className="relative flex h-28 overflow-hidden rounded-xl"
                    >
                        {/* Competência fechada (anterior) */}
                        <Segmento
                            pesoDias={PESO_CONTEXTO}
                            contexto
                            estreito={estreitoCtx}
                            compact={
                                <span className="text-xs font-semibold">
                                    {mmAnterior.format('MM/YY')}
                                </span>
                            }
                            tooltip={
                                <DetalheComp
                                    titulo={mmAnterior.format('MM/YYYY')}
                                    periodo={`até ${base.anterior.fim.format(
                                        'DD/MM',
                                    )}`}
                                />
                            }
                        >
                            <span className="text-sm font-semibold">
                                {mmAnterior.format('MM/YYYY')}
                            </span>
                            <Badge variant="secondary">
                                {base.anterior.inicio.format('DD/MM')}{' '}
                                <ArrowRightIcon className="size-4 text-primary" />{' '}
                                {base.anterior.fim.format('DD/MM')}
                            </Badge>
                        </Segmento>

                        {/* Competência atual */}
                        <Segmento
                            pesoDias={atualDias}
                            estreito={estreitoAtual}
                            compact={
                                <span
                                    className={cn('text-sm font-bold', {
                                        'text-warning': atualNoLimite,
                                    })}
                                >
                                    {atualDias}d
                                </span>
                            }
                            tooltip={
                                <DetalheComp
                                    titulo={`${mmAtual.format(
                                        'MM/YYYY',
                                    )} · ${atualDias}d`}
                                    periodo={`${start.format(
                                        'DD/MM',
                                    )} → ${atualFim.format('DD/MM')}`}
                                    nota={
                                        atualNoLimite ? 'no limite' : undefined
                                    }
                                />
                            }
                        >
                            <span
                                className={cn('text-sm font-bold', {
                                    'text-warning': atualNoLimite,
                                })}
                            >
                                {mmAtual.format('MM/YYYY')} · {atualDias}d
                            </span>
                            <span className="inline-flex items-center text-xs text-secondary-foreground">
                                {start.format('DD/MM')}
                                <ArrowRightIcon className="mx-1 size-3 text-primary" />
                                {atualFim.format('DD/MM')}
                            </span>
                            {atualNoLimite && (
                                <Badge variant="warning">no limite</Badge>
                            )}
                        </Segmento>

                        {/* Competência de transição */}
                        <Segmento
                            pesoDias={transDias}
                            estreito={estreitoTrans}
                            compact={
                                <span
                                    className={cn('text-sm font-semibold', {
                                        'text-warning': transNoLimite,
                                    })}
                                >
                                    {transDias}d
                                </span>
                            }
                            tooltip={
                                <DetalheComp
                                    titulo={`${mmTrans.format(
                                        'MM/YYYY',
                                    )} · ${transDias}d`}
                                    periodo={`${transInicio.format(
                                        'DD/MM',
                                    )} → ${transFim.format('DD/MM')}`}
                                    nota={
                                        transNoLimite ? 'no limite' : undefined
                                    }
                                />
                            }
                        >
                            <span
                                className={cn('text-sm font-semibold', {
                                    'text-warning': transNoLimite,
                                })}
                            >
                                {mmTrans.format('MM/YYYY')} · {transDias}d
                            </span>
                            <span className="inline-flex items-center text-xs text-secondary-foreground">
                                {transInicio.format('DD/MM')}
                                <ArrowRightIcon className="mx-1 size-3 text-primary" />
                                {transFim.format('DD/MM')}
                            </span>
                            {transNoLimite && (
                                <Badge variant="warning">no limite</Badge>
                            )}
                        </Segmento>

                        {/* Competência futura (contexto, read-only) */}
                        <Segmento
                            pesoDias={PESO_CONTEXTO}
                            contexto
                            estreito={estreitoCtx}
                            compact={
                                <span className="text-xs font-semibold">
                                    fut.
                                </span>
                            }
                            tooltip={
                                <DetalheComp
                                    titulo={mmTrans
                                        .add(1, 'month')
                                        .format('MM/YY')}
                                    periodo={`a partir de ${futuraInicio.format(
                                        'DD/MM',
                                    )}`}
                                />
                            }
                        >
                            <span className="text-sm font-semibold">
                                {mmTrans.add(1, 'month').format('MM/YYYY')}
                            </span>
                            <Badge variant="secondary">
                                {futuraInicio.format('DD/MM')}{' '}
                                <ArrowRightIcon className="size-4 text-primary" />
                                {base.futuraFim.format('DD/MM')}
                            </Badge>
                        </Segmento>

                        {/* Listras de limite — só junto da divisa arrastável, ao
                            bater no teto de 45d/1d, esmaecendo para longe dela */}
                        {limiteEsq && (
                            <div
                                className="pointer-events-none absolute inset-y-0 w-4 -translate-x-full"
                                style={{
                                    left: `${fracDivisa1}%`,
                                    ...fade('left'),
                                }}
                            />
                        )}
                        {limiteDir && (
                            <div
                                className="pointer-events-none absolute inset-y-0 w-4"
                                style={{
                                    left: `${fracDivisa1}%`,
                                    ...fade('right'),
                                }}
                            />
                        )}
                    </div>

                    {/* Divisa travada (fechada | atual) */}
                    <LinhaDivisa frac={fracFechada} />
                    <SeloTravado frac={fracFechada} />

                    {/* Divisa arrastável (atual | transição) */}
                    <div className="group">
                        <LinhaDivisa frac={fracDivisa1} primary />
                        <div
                            role="slider"
                            tabIndex={0}
                            aria-label="Fronteira entre a competência atual e a de transição"
                            aria-valuemin={b1Min}
                            aria-valuemax={b1Max}
                            aria-valuenow={b1}
                            {...handlers}
                            className={cn(
                                'absolute inset-y-0 flex w-8',
                                '-translate-x-1/2 cursor-ew-resize',
                                'touch-none items-center justify-center',
                                'outline-none',
                            )}
                            style={{ left: `${fracDivisa1}%` }}
                        >
                            <span
                                className={cn(
                                    'flex size-7 items-center justify-center',
                                    'rounded-full ring-2 ring-primary dark:ring-primary-foreground',
                                    'bg-background text-primary shadow-md',
                                    'transition group-focus-visible:ring-4',
                                    'group-focus-visible:ring-primary/40',
                                    'group-hover:ring-primary',
                                )}
                            >
                                <ArrowLeftRightIcon
                                    className={cn(
                                        'size-4 text-primary dark:text-primary-foreground',
                                        'group-hover:text-primary',
                                        'transition-colors duration-200',
                                    )}
                                />
                            </span>
                        </div>
                    </div>

                    {/* Divisa travada (transição | futura) — fixa pelo novo dia */}
                    <LinhaDivisa frac={fracDivisa2} />
                    <SeloTravado frac={fracDivisa2} />

                    {/* Rótulos das travadas (acima) */}
                    <RotuloDivisa
                        frac={fracFechada}
                        posicao="topo"
                        datas={
                            <>
                                {base.anterior.fim.format('DD/MM')}{' '}
                                <span className="text-primary">|</span>{' '}
                                {start.format('DD/MM')}
                            </>
                        }
                        legenda={
                            <span className="inline-flex items-center gap-1">
                                <LockIcon className="size-3" /> travada
                                (fechada)
                            </span>
                        }
                    />
                    <RotuloDivisa
                        frac={fracDivisa2}
                        posicao="topo"
                        datas={
                            <>
                                {transFim.format('DD/MM')}{' '}
                                <span className="text-primary">|</span>{' '}
                                {futuraInicio.format('DD/MM')}
                            </>
                        }
                        legenda={`daqui em diante, dia ${diaVigente}`}
                    />

                    {/* Rótulo da arrastável (abaixo) */}
                    <RotuloDivisa
                        frac={fracDivisa1}
                        posicao="base"
                        datas={`${atualFim.format(
                            'DD/MM',
                        )} | ${transInicio.format('DD/MM')}`}
                        legenda={`fim de ${mmAtual.format(
                            'MM',
                        )} · início de ${mmTrans.format('MM')}`}
                    />
                </div>
            </div>

            <div className="rounded-lg border border-dashed p-3 text-sm text-secondary-foreground">
                Arraste a divisa{' '}
                <div className="rounded-full border border-primary-foreground inline-flex items-center p-1 size-6 align-bottom">
                    <ArrowLeftRightIcon className="inline-block size-4 align-text-bottom" />
                </div>{' '}
                para mover a fronteira entre a competência atual e a de
                transição. <br /> As divisas travadas{' '}
                <div className="rounded-full border border-primary-foreground inline-flex items-center p-1 size-6 align-bottom">
                    <LockIcon className="inline-block size-4 align-text-bottom" />
                </div>{' '}
                não podem ser alteradas nessa competência.
            </div>
        </div>
    )
}
